import React from "react";
import moment from "moment";
import { some } from "lodash";
import { accountTypesToEnglishMapping } from "statics/accountTypes";
import IconBanner from "components/IconBanner";
import IndividualRolloverCard from "./IndividualRolloverCard";
import Rollover401kProgressiveOnboarding from "./Rollover401kProgressiveOnboarding";
import { FiAlertTriangle, FiRefreshCw, FiStar } from "react-icons/fi";
import { connect, type ConnectedProps } from "react-redux";
import { isIndividualAccountOverview } from "store/selectors/user";
import type { RootState } from "types/state";
import type { IraAccount } from "types/models";

const mapStateToProps = (state: RootState) => {
  return {
    isAccountOverview: isIndividualAccountOverview(state),
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  account: IraAccount;
  accountType: string;
  auth0AccountSessionId?: string;
  accounts?: unknown[];
  accountNumber?: string;
}

type Props = OwnProps & PropsFromRedux;

class RolloverAccountStatus extends React.Component<Props> {
  getRolloverStatus = () => {
    const { account } = this.props;
    const statusPriority = [
      "PENDING",
      "COMPLETE",
      "ERROR",
      "CANCELLED",
      "EXPIRED",
    ];

    const foundStatus = statusPriority.find((status) =>
      some(account.rollovers, { status })
    );

    return foundStatus;
  };

  isRecentlyCompleted(account: IraAccount) {
    const completed = account.rollovers!.find((r) => r!.status === "COMPLETE");
    if (!completed) return false;

    const daysSinceCompletion = moment().diff(completed.updatedAt, "days");

    return daysSinceCompletion <= 7;
  }

  render() {
    const { account, accountType, accountNumber } = this.props;

    const rolloverStatus = this.getRolloverStatus();

    const hasCheckTransfer = some(account.rollovers, {
      fundsTransferMethod: "CHECK",
    });

    // PENDING status
    if (rolloverStatus === "PENDING") {
      if (hasCheckTransfer) {
        return (
          <IndividualRolloverCard
            // @ts-expect-error inert pre-existing pass: IndividualRolloverCard
            // accepts neither `icon` nor `color` (it renders its own FiList icon
            // and hardcodes color="primary"); both are dead props on this call.
            icon={<FiRefreshCw color="white" stroke="#60A4BF" size={16} />}
            color="primary"
            title={`Ready to send your ${
              accountTypesToEnglishMapping[
                accountType as keyof typeof accountTypesToEnglishMapping
              ]
            } rollover check?`}
            subheader="For instructions, please refer to the information provided here."
            accountNumber={accountNumber}
            accountType={accountType}
          />
        );
      }

      return (
        <IconBanner
          icon={<FiRefreshCw color="white" stroke="#60A4BF" size={16} />}
          color="primary"
          title={`Your ${
            accountTypesToEnglishMapping[
              accountType as keyof typeof accountTypesToEnglishMapping
            ]
          } Paychex rollover funds are being processed`}
          subheader="We're currently processing your deposit. This typically takes 3-5 business days. You'll receive an email notification once your funds are available."
        />
      );
    }

    // COMPLETE status
    if (rolloverStatus === "COMPLETE") {
      const isRecentlyCompleted = this.isRecentlyCompleted(account);

      if (isRecentlyCompleted) {
        return (
          <IconBanner
            circle={false}
            color="primary"
            title={`Congrats! You've completed your ${
              accountTypesToEnglishMapping[
                accountType as keyof typeof accountTypesToEnglishMapping
              ]
            } Paychex rollover 🎉`}
            subheader="Your Paychex rollover has been completed. You can now view your account balance and latest transactions."
          />
        );
      }
      // if COMPLETE and not recently complete we should just return nothing
      return null;
    }

    // ERROR statuses
    if (
      // rolloverStatus is string | undefined; .includes(undefined) is false,
      // matching the original runtime behavior. Cast to satisfy the index.
      ["CANCELLED", "EXPIRED", "ERROR"].includes(rolloverStatus as string)
    ) {
      const accountTypeDisplay =
        accountTypesToEnglishMapping[
          accountType as keyof typeof accountTypesToEnglishMapping
        ] || accountType;

      let title;
      if (rolloverStatus === "ERROR") {
        title = `There has been an error processing your ${accountTypeDisplay} Paychex rollover.`;
      } else if (rolloverStatus === "EXPIRED") {
        title = `Your ${accountTypeDisplay} Paychex rollover has expired.`;
      } else if (rolloverStatus === "CANCELLED") {
        title = `Your ${accountTypeDisplay} Paychex rollover has been cancelled.`;
      }

      return (
        <Rollover401kProgressiveOnboarding
          icon={<FiAlertTriangle color="white" stroke="#B12121" size={16} />}
          color="error"
          title={title}
          subheader="Please contact Paychex for more information."
        />
      );
    }

    return (
      <Rollover401kProgressiveOnboarding
        icon={<FiStar color="white" stroke="#60A4BF" size={16} />}
        color="primary"
        title={`Your ${
          accountTypesToEnglishMapping[
            accountType as keyof typeof accountTypesToEnglishMapping
          ]
        } account is ready—just one step left.`}
        subheader="To finalize your rollover, go to the Paychex Portal to confirm the transaction. You will receive an email as soon as that is complete."
        disclaimer="There will be a one-time processing fee of $20.00 to set up your account."
      />
    );
  }
}

export default connector(RolloverAccountStatus);
